/**
 * CRSPanel — Coordinate system tools
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { Transform, Info, SwapHoriz } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { openCRSInfoDialog, openTransformTool } from '../../../store/slices/coordinateSystemSlice';
import CRSStatusDisplay from '../../CRSStatusDisplay';
import CRSInfoDialog from '../../CRSInfoDialog';
import CoordinateTransformPanel from '../../CoordinateTransformPanel';
import ReprojectionDialog from '../../ReprojectionDialog';

const CRSPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const mapCRS = useAppSelector((s) => s.coordinateSystem.mapCRS);

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
            mb: 1,
          }}
        >
          Current Map CRS
        </Typography>
        <Box
          sx={{
            p: 1,
            borderRadius: tokens.radius.md,
            backgroundColor: palette.accent.primaryDim,
            border: `1px solid ${palette.accent.primary}30`,
          }}
        >
          <CRSStatusDisplay />
        </Box>
      </Box>

      <Divider />

      <List dense disablePadding>
        <ListItemButton
          onClick={() => dispatch(openCRSInfoDialog(mapCRS))}
          sx={{ borderRadius: tokens.radius.sm, mx: -0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: palette.accent.primary }}>
            <Info sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="CRS Information"
            secondary="View EPSG details and projection info"
            primaryTypographyProps={{ sx: { fontSize: tokens.fontSize.md, fontWeight: 500 } }}
            secondaryTypographyProps={{ sx: { fontSize: tokens.fontSize.xs } }}
          />
        </ListItemButton>

        <ListItemButton
          onClick={() => dispatch(openTransformTool())}
          sx={{ borderRadius: tokens.radius.sm, mx: -0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: palette.accent.primary }}>
            <Transform sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Coordinate Transform"
            secondary="Convert coordinates between CRS"
            primaryTypographyProps={{ sx: { fontSize: tokens.fontSize.md, fontWeight: 500 } }}
            secondaryTypographyProps={{ sx: { fontSize: tokens.fontSize.xs } }}
          />
        </ListItemButton>
      </List>

      <CRSInfoDialog />
      <CoordinateTransformPanel />
      <ReprojectionDialog />
    </Box>
  );
};

export default CRSPanel;
